/**
 * Values Wheel Component
 *
 * A pie chart visualization showing value distribution and alignment.
 * Slice size is proportional to weight percentage.
 * Color indicates congruence level (green/yellow/red).
 */

import { useState, useMemo } from 'react';
import type { Value } from '@/types/value';
import { getCongruenceColor, getCongruenceLevel } from '@/types/value';

interface ValuesWheelProps {
  values: Value[];
  size?: number;
  onValueClick?: (value: Value) => void;
}

interface PieSlice {
  value: Value;
  startAngle: number;
  endAngle: number;
  percentage: number;
  color: string;
}

export function ValuesWheel({ values, size = 280, onValueClick }: ValuesWheelProps) {
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const slices = useMemo(() => {
    if (values.length === 0) return [];

    const totalWeight = values.reduce((sum, v) => sum + v.weight, 0) || 1;
    let currentAngle = -90; // Start from top

    return values.map((value) => {
      const percentage = (value.weight / totalWeight) * 100;
      const sliceAngle = (percentage / 100) * 360;
      const startAngle = currentAngle;
      const endAngle = currentAngle + sliceAngle;
      currentAngle = endAngle;

      const colors = getCongruenceColor(value.congruenceScore);

      return {
        value,
        startAngle,
        endAngle,
        percentage,
        color: colors.fill,
      } as PieSlice;
    });
  }, [values]);

  const padding = 50; // Space for labels
  const center = size / 2;
  const radius = (size / 2) - padding;
  const innerRadius = radius * 0.55; // Donut chart

  // Convert polar to cartesian coordinates
  const polarToCartesian = (angle: number, r: number) => {
    const radians = (angle * Math.PI) / 180;
    return {
      x: center + r * Math.cos(radians),
      y: center + r * Math.sin(radians),
    };
  };

  // Create arc path for a slice
  const createArcPath = (startAngle: number, endAngle: number, outerR: number, innerR: number) => {
    const start1 = polarToCartesian(startAngle, outerR);
    const end1 = polarToCartesian(endAngle, outerR);
    const start2 = polarToCartesian(endAngle, innerR);
    const end2 = polarToCartesian(startAngle, innerR);

    const largeArc = endAngle - startAngle > 180 ? 1 : 0;

    return [
      `M ${start1.x} ${start1.y}`,
      `A ${outerR} ${outerR} 0 ${largeArc} 1 ${end1.x} ${end1.y}`,
      `L ${start2.x} ${start2.y}`,
      `A ${innerR} ${innerR} 0 ${largeArc} 0 ${end2.x} ${end2.y}`,
      'Z',
    ].join(' ');
  };

  if (values.length === 0) {
    return (
      <div
        className="flex items-center justify-center bg-stone-50 rounded-2xl border-2 border-dashed border-stone-200"
        style={{ width: size, height: size }}
      >
        <div className="text-center text-stone-500">
          <p className="font-medium">No values yet</p>
          <p className="text-sm">Add values to see your wheel</p>
        </div>
      </div>
    );
  }

  const hoveredSlice = slices.find((s) => s.value.id === hoveredId);

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="drop-shadow-lg">
        {/* Background circle */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke="#f5f5f4"
          strokeWidth="2"
        />

        {/* Pie slices */}
        {slices.map((slice) => {
          const isHovered = slice.value.id === hoveredId;
          const path = createArcPath(
            slice.startAngle,
            slice.endAngle,
            isHovered ? radius + 5 : radius,
            isHovered ? innerRadius - 2 : innerRadius
          );

          // Calculate label position at middle of arc, outside the pie
          const midAngle = (slice.startAngle + slice.endAngle) / 2;
          const labelRadius = radius + 12;
          const labelPos = polarToCartesian(midAngle, labelRadius);
          
          // Only show label if slice is large enough (> 4%)
          const showLabel = slice.percentage > 4;
          
          // Get first word or truncate for small labels
          const words = slice.value.name.split(' ');
          const displayName = words[0].length > 10 
            ? words[0].substring(0, 8) + '…' 
            : words[0];

          return (
            <g key={slice.value.id}>
              <path
                d={path}
                fill={slice.color}
                stroke="white"
                strokeWidth={2}
                className="cursor-pointer transition-all duration-200"
                style={{
                  opacity: hoveredId && !isHovered ? 0.6 : 1,
                  filter: isHovered ? 'brightness(1.1)' : 'none',
                }}
                onMouseEnter={() => setHoveredId(slice.value.id)}
                onMouseLeave={() => setHoveredId(null)}
                onClick={() => onValueClick?.(slice.value)}
              />
              {/* Value name label */}
              {showLabel && (
                <text
                  x={labelPos.x}
                  y={labelPos.y}
                  textAnchor={midAngle > -90 && midAngle < 90 ? 'start' : 'end'}
                  dominantBaseline="middle"
                  className="text-[9px] font-medium fill-stone-600 pointer-events-none select-none"
                  style={{
                    opacity: hoveredId && !isHovered ? 0.4 : 1,
                  }}
                >
                  {displayName}
                </text>
              )}
            </g>
          );
        })}

        {/* Center circle background */}
        <circle
          cx={center}
          cy={center}
          r={innerRadius - 8}
          fill="white"
        />
      </svg>

      {/* Center content */}
      <div
        className="absolute flex flex-col items-center justify-center text-center"
        style={{
          top: center - innerRadius + 20,
          left: center - innerRadius + 20,
          width: (innerRadius - 20) * 2,
          height: (innerRadius - 20) * 2,
        }}
      >
        {hoveredSlice ? (
          <>
            <div className="text-xs text-stone-500 uppercase tracking-wide">
              {getCongruenceLevel(hoveredSlice.value.congruenceScore)}
            </div>
            <div className="text-lg font-bold text-stone-900 leading-tight mt-0.5">
              {hoveredSlice.percentage.toFixed(0)}%
            </div>
            <div className="text-xs font-medium text-stone-700 line-clamp-2 px-2 mt-1">
              {hoveredSlice.value.name}
            </div>
            <div className="text-xs text-stone-500 mt-0.5">
              Score: {Math.round(hoveredSlice.value.congruenceScore * 100)}%
            </div>
          </>
        ) : (
          <>
            <div className="text-xs text-stone-500 uppercase tracking-wide">Values</div>
            <div className="text-2xl font-bold text-stone-900">{values.length}</div>
            <div className="text-xs text-stone-500">total</div>
          </>
        )}
      </div>

      {/* Legend (below chart) */}
      <div className="absolute -bottom-6 left-0 right-0 flex justify-center gap-4 text-xs">
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
          <span className="text-stone-600">Strong</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-500" />
          <span className="text-stone-600">Partial</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
          <span className="text-stone-600">Gap</span>
        </div>
      </div>
    </div>
  );
}

